/**
 * useExecutionLogs
 * React hook for real-time streaming of execution logs from Firestore
 * Used for viewing live execution logs as they are generated
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, collection, query } from 'firebase/firestore';
import { db } from '@/config/firebase';
import type { ExecutionLog } from '@/components/execution/ExecutionLogViewer';
import type { ExecutionMetricsMap, DiagSummary } from '@simcheck/shared-types';

export interface ExecutionArtifact {
  id: string;
  type: string;
  filename?: string;
  sizeBytes?: number;
}

interface ExecutionData {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  flowName?: string;
  startedAt?: Date;
  completedAt?: Date;
  logs: ExecutionLog[];
}

/**
 * Connectivity-related fields lifted from the execution doc so the Test Run
 * Details modal can render <ConnectivitySnapshot> without a second listener.
 * All fields optional — older runs that pre-date the cellular_attach_verify
 * seeding still render via the legacy registeredNetwork* fallback.
 */
export interface ExecutionConnectivity {
  metrics?: ExecutionMetricsMap;
  bearerClass?: string;
  registeredNetwork?: string;
  registeredNetworkName?: string;
  registeredNetworkType?: string | number;
  registeredNetworkMcc?: string;
  registeredNetworkMnc?: string;
  // Location context written by FlowExecutor pre-flow enrichment so the
  // ConnectivitySnapshot panel can answer "where did this run happen?"
  // alongside "which network did we attach to?".
  locationName?: string;
  countryCode?: string;
  regionName?: string;
  // Live speedtest sample written by central-server from the edge's
  // data_test_progress WS messages; drives <SpeedTestGauges> in real time.
  liveSample?: { phase: 'download' | 'upload'; mbps: number; t?: string; commandId?: string };
}

/**
 * One named step of a run, from `executions/{id}.setupSteps.<key>`: the setup
 * sub-steps (#381: modem allocated, eSIM move / download, network wait, RAT /
 * PLMN locks), the flow's own steps and the release (#389, phase `execution` /
 * `cleanup`). Sorted by when each started.
 */
export interface SetupStepView {
  key: string;
  label: string;
  phase: 'setup' | 'execution' | 'cleanup';
  state: 'running' | 'done' | 'warning' | 'failed' | 'skipped';
  detail?: string;
  modemImei?: string;
  startedAt?: Date;
  endedAt?: Date;
}

function toDate(v: any): Date | undefined {
  if (!v) return undefined;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toSetupSteps(raw: any): SetupStepView[] {
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .map(([key, v]: [string, any]) => ({
      key,
      label: v?.label || key,
      phase: v?.phase === 'execution' || v?.phase === 'cleanup' ? v.phase : 'setup',
      state: v?.state || 'running',
      detail: v?.detail || undefined,
      modemImei: v?.modemImei || undefined,
      startedAt: toDate(v?.startedAt),
      endedAt: toDate(v?.endedAt),
    }))
    .sort((a, b) =>
      ((a.startedAt ?? a.endedAt)?.getTime() ?? 0) - ((b.startedAt ?? b.endedAt)?.getTime() ?? 0));
}

/**
 * Queue/dispatch wait-state lifted from the execution doc so the Test Run
 * Details modal can show *why* a run is sitting in "Initializing" (waiting for
 * a free modem) instead of a blank log pane. Written by
 * QueueService._recordDispatchFailure on each dispatch attempt that finds no
 * free modem; all optional (a run that dispatches immediately never has them).
 */
export interface ExecutionPending {
  reason?: string;
  detail?: string;
  lastDispatchAttemptAt?: Date;
  attemptCount?: number;
}

interface UseExecutionLogsResult {
  logs: ExecutionLog[];
  status: ExecutionData['status'] | null;
  flowName: string | null;
  currentNode: string | null;
  isLive: boolean;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  connectivity: ExecutionConnectivity;
  pending: ExecutionPending;
  // Decoded DIAG (L3-trace) summary lifted from the execution doc — drives
  // the <NetworkDiagnosticsPanel> "Signaling" tab.
  diagSummary?: DiagSummary;
  // Artifacts subcollection (pcap / l3_trace / at_log / …) for the modal's
  // Artifacts + Signaling tabs (the modal previously had no artifact UI).
  artifacts: ExecutionArtifact[];
  // Why an artifact was NOT produced (`{ kind: reason }`), from the edge's
  // capture-session skip report. Lets the UI say "no signaling — still warming"
  // instead of showing an empty panel with no explanation.
  artifactSkips?: Record<string, string>;
  setupSteps: SetupStepView[];
}

/**
 * Stream execution logs in real-time from Firestore
 * @param executionId - The execution ID to stream logs for
 * @returns Object with logs, status, and loading state
 */
export function useExecutionLogs(executionId: string | null): UseExecutionLogsResult {
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [status, setStatus] = useState<ExecutionData['status'] | null>(null);
  const [flowName, setFlowName] = useState<string | null>(null);
  const [currentNode, setCurrentNode] = useState<string | null>(null);
  const [connectivity, setConnectivity] = useState<ExecutionConnectivity>({});
  const [pending, setPending] = useState<ExecutionPending>({});
  const [diagSummary, setDiagSummary] = useState<DiagSummary | undefined>(undefined);
  const [artifacts, setArtifacts] = useState<ExecutionArtifact[]>([]);
  const [artifactSkips, setArtifactSkips] = useState<Record<string, string> | undefined>(undefined);
  const [setupSteps, setSetupSteps] = useState<SetupStepView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Manual refresh function
  const refresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  useEffect(() => {
    if (!executionId) {
      setLogs([]);
      setStatus(null);
      setFlowName(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Set up real-time listener
    const unsubscribe = onSnapshot(
      doc(db, 'executions', executionId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setError('Execution not found');
          setIsLoading(false);
          return;
        }

        const data = snapshot.data();

        // Convert logs to proper format
        const executionLogs: ExecutionLog[] = (data.logs || []).map((log: any) => ({
          timestamp: log.timestamp
            ? (typeof log.timestamp === 'string'
                ? new Date(log.timestamp)
                : log.timestamp.toDate?.() || new Date(log.timestamp))
            : new Date(),
          level: log.level === 'warning' ? 'warn' : log.level,
          message: log.message
        }));

        setLogs(executionLogs);
        setStatus(data.status || 'pending');
        setFlowName(data.flowName || null);
        setCurrentNode(data.currentNode || null);
        setConnectivity({
          metrics: data.metrics,
          bearerClass: data.bearerClass,
          registeredNetwork: data.registeredNetwork,
          registeredNetworkName: data.registeredNetworkName,
          registeredNetworkType: data.registeredNetworkType,
          registeredNetworkMcc: data.registeredNetworkMcc,
          registeredNetworkMnc: data.registeredNetworkMnc,
          locationName: data.locationName,
          countryCode: data.countryCode,
          regionName: data.regionName,
          liveSample: data.liveSample,
        });
        setPending({
          reason: data.pendingReason,
          detail: data.pendingDetail,
          lastDispatchAttemptAt: data.lastDispatchAttemptAt?.toDate?.() || undefined,
          attemptCount: typeof data.attemptCount === 'number' ? data.attemptCount : undefined,
        });
        setDiagSummary(data.diagSummary as DiagSummary | undefined);
        setArtifactSkips(data.artifactSkips as Record<string, string> | undefined);
        setSetupSteps(toSetupSteps(data.setupSteps));
        setIsLoading(false);
      },
      (err) => {
        console.error('Error streaming execution logs:', err);
        setError(err.message);
        setIsLoading(false);
      }
    );

    // Artifacts subcollection — the Test Run Details modal had no artifact UI;
    // the Signaling + Artifacts tabs read this.
    const unsubArtifacts = onSnapshot(
      query(collection(db, 'executions', executionId, 'artifacts')),
      (snap) => setArtifacts(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      (err) => console.warn('Artifact subscription error:', err),
    );

    // Cleanup listener on unmount or when executionId changes
    return () => {
      unsubscribe();
      unsubArtifacts();
    };
  }, [executionId, refreshTrigger]);

  // Determine if execution is live (still running)
  const isLive = status === 'running' || status === 'pending';

  return {
    logs,
    status,
    flowName,
    currentNode,
    isLive,
    isLoading,
    error,
    refresh,
    connectivity,
    pending,
    diagSummary,
    artifacts,
    artifactSkips,
    setupSteps,
  };
}

/**
 * Hook to stream logs for multiple executions (e.g., cluster children)
 * @param executionIds - Array of execution IDs to stream
 * @returns Map of executionId to logs
 */
export function useMultipleExecutionLogs(executionIds: string[]): Map<string, ExecutionLog[]> {
  const [logsMap, setLogsMap] = useState<Map<string, ExecutionLog[]>>(new Map());

  useEffect(() => {
    if (executionIds.length === 0) {
      setLogsMap(new Map());
      return;
    }

    const unsubscribes: (() => void)[] = [];

    for (const executionId of executionIds) {
      const unsubscribe = onSnapshot(
        doc(db, 'executions', executionId),
        (snapshot) => {
          if (!snapshot.exists()) return;

          const data = snapshot.data();
          const executionLogs: ExecutionLog[] = (data.logs || []).map((log: any) => ({
            timestamp: log.timestamp
              ? (typeof log.timestamp === 'string'
                  ? new Date(log.timestamp)
                  : log.timestamp.toDate?.() || new Date(log.timestamp))
              : new Date(),
            level: log.level === 'warning' ? 'warn' : log.level,
            message: log.message
          }));

          setLogsMap(prev => {
            const next = new Map(prev);
            next.set(executionId, executionLogs);
            return next;
          });
        },
        (err) => {
          console.error(`Error streaming logs for ${executionId}:`, err);
        }
      );

      unsubscribes.push(unsubscribe);
    }

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [executionIds.join(',')]); // Re-run when the list of IDs changes

  return logsMap;
}
